import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { LoadDropdown, Option, useInputHelper } from 'framework/form';
import useNewProjectService from '../../newProjectService';
import { StylesConfig } from 'react-select';
import { ProjectSearchDto } from '../../types';
import { GroupedProjectsByAccount, ProjectDropdownOption, ProjectOptionValue } from './LoadProjectsDropdown.types';
import {
  compareAccounts,
  getAccountId,
  getOptionId,
  isProjectOptionValue,
  PAGE_SIZE,
  WITHOUT_ACCOUNT_LABEL,
} from './LoadProjectsDropdown.utils';

type LoadProjectsDropdownProps = {
  source: string;
  [key: string]: unknown;
};

const customStyles: StylesConfig<ProjectDropdownOption, boolean> = {
  option: (provided, state) => ({
    ...provided,
    paddingLeft: state.data.account ? provided.paddingLeft : '24px !important',
    fontWeight: state.data.account ? 600 : provided.fontWeight,
  }),
};

const LoadProjectsDropdown: React.FC<LoadProjectsDropdownProps> = ({ source, ...props }: LoadProjectsDropdownProps) => {
  const { search } = useNewProjectService();
  const { getValue, getRawOnChangeCallback } = useInputHelper<ProjectOptionValue[]>({ source });
  const projectAccountIdByProjectIdRef = useRef<Record<string, string>>({});

  const selectedValues = useMemo(() => ((getValue() || []) as unknown[]).filter(isProjectOptionValue), [getValue]);

  const selectedValueIds = useMemo(() => new Set(selectedValues.map(getOptionId)), [selectedValues]);

  const selectedAccountIds = useMemo(
    () => new Set(selectedValues.filter(value => value.account).map(value => String(value.id))),
    [selectedValues],
  );

  const hasSelectedAccounts = selectedAccountIds.size > 0;

  useEffect(() => {
    if (!selectedValues.length || !hasSelectedAccounts) {
      return;
    }

    const filteredValues = selectedValues.filter(value => {
      if (value.account) {
        return true;
      }

      const projectAccountId = projectAccountIdByProjectIdRef.current[String(value.id)];
      return !projectAccountId || !selectedAccountIds.has(projectAccountId);
    });

    if (filteredValues.length === selectedValues.length) {
      return;
    }

    const rawOnChange = getRawOnChangeCallback();
    rawOnChange?.({
      [source]: filteredValues,
    });
  }, [getRawOnChangeCallback, hasSelectedAccounts, selectedAccountIds, selectedValues, source]);

  const loadProjects = useCallback(async () => {
    const request = {
      pageNumber: 0,
      filter: { isArchived: { ne_or_null: true } },
      pageSize: PAGE_SIZE,
    };

    return search(request);
  }, [search]);

  const mapResult = useCallback((projects: ProjectSearchDto[]): ProjectDropdownOption[] => {
    if (!projects?.length) {
      return [];
    }

    const groupedByAccountId = projects.reduce((acc: GroupedProjectsByAccount, project) => {
      const accountId = getAccountId(project);
      projectAccountIdByProjectIdRef.current[String(project.id)] = accountId;

      if (!acc[accountId]) {
        acc[accountId] = [];
      }

      acc[accountId].push(project);
      return acc;
    }, {});

    const sortedEntries = Object.entries(groupedByAccountId).sort(compareAccounts);

    return sortedEntries.flatMap(([accountId, accountProjects]) => {
      const sectionName = accountProjects[0]?.accountName || accountId;

      const section: ProjectDropdownOption = {
        value: { id: String(accountId), name: String(sectionName), account: true },
        label: sectionName,
        disabled: accountId === WITHOUT_ACCOUNT_LABEL,
        account: true,
        accountId,
      };

      const options: ProjectDropdownOption[] = accountProjects.map(project => ({
        // TODO Temp legacyId
        value: { id: String(project.legacyId), name: project.name, account: false },
        label: project.name,
        accountId,
      }));

      return [section, ...options];
    });
  }, []);

  const filterOption = useCallback(
    (option: { data: Option }, rawInput: string) => {
      const data = option.data as unknown as ProjectDropdownOption;
      const optionValueId = getOptionId(data.value);
      if (selectedValueIds.has(optionValueId)) {
        return false;
      }

      if (!data.account && selectedAccountIds.has(String(data.accountId || ''))) {
        return false;
      }

      if (!rawInput) {
        return true;
      }

      return data.label?.toString().toLowerCase().includes(rawInput.toLowerCase());
    },
    [selectedAccountIds, selectedValueIds],
  );

  const getOptionValue = useCallback((option: Option) => {
    const value = (option as ProjectDropdownOption).value;
    return isProjectOptionValue(value) ? getOptionId(value) : String(option.value);
  }, []);

  return (
    <LoadDropdown
      source={source}
      loadDataMethod={loadProjects}
      mapResult={mapResult}
      styles={customStyles}
      filterOption={filterOption}
      getOptionValue={getOptionValue}
      multiple
      showLoader
      placeholder="Select project"
      {...props}
    />
  );
};

export default LoadProjectsDropdown;
